import { useState, useEffect, useCallback, useRef } from 'react';
import axios_instance from '../api/axios_instance';
import { API_URL } from '../config';
import {
  Loader2, Edit, Plus,
  Package, Search, AlertCircle, ExternalLink,
  ChevronLeft, Archive, RefreshCcw, CheckCircle2, X,
  Sparkles, Flame, Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'available',    label: 'متوفر', color: 'bg-emerald-500' },
  { value: 'low_stock',    label: 'محدود', color: 'bg-orange-400' },
  { value: 'out_of_stock', label: 'نفذ',   color: 'bg-red-500' },
];

const STOCK_BADGE = {
  available:    { label: 'متوفر',  cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  low_stock:    { label: 'محدود',  cls: 'text-orange-500 bg-orange-50 border-orange-200' },
  out_of_stock: { label: 'نفذ',    cls: 'text-red-500 bg-red-50 border-red-200' },
};

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  const backendURL = API_URL;
  const LIMIT = 10;

  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) setPage(p => p + 1);
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const buildQuery = useCallback((pageNum) => {
    const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
    if (typeFilter !== 'all') params.set('filter', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    return params.toString();
  }, [typeFilter, statusFilter, debouncedSearch]);

  const fetchProducts = useCallback(async (pageNum, reset = false) => {
    setLoading(true);
    try {
      const res = await axios_instance.get(`/api/products?${buildQuery(pageNum)}`);
      const { products: fetched, pagination } = res.data;
      setProducts(prev => reset ? fetched : [...prev, ...fetched]);
      setHasMore(pagination.page < pagination.pages);
    } catch {
      showToast('خطأ في تحميل المنتجات', 'error');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [buildQuery]);

  // Reset on filter or search change
  useEffect(() => {
    setPage(1);
    setProducts([]);
    setHasMore(true);
    fetchProducts(1, true);
  }, [typeFilter, statusFilter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    if (page > 1) fetchProducts(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const handleToggleArchive = async (id, currentIsArchived) => {
    try {
      await axios_instance.put(`/api/products/${id}`, { isArchived: !currentIsArchived });
      showToast(!currentIsArchived ? 'تمت أرشفة المنتج بنجاح' : 'تم استعادة المنتج بنجاح');
      setProducts(prev => prev.map(p => p._id === id ? { ...p, isArchived: !currentIsArchived } : p));
    } catch {
      showToast('عذراً، فشلت عملية التحديث', 'error');
    }
  };

  const getLabel = (obj) => obj?.ar || obj?.en || obj?.fr || '';

  const filteredProducts = products;

  if (initialLoading) return (
    <div className="flex justify-center py-20 min-h-screen">
      <Loader2 className="animate-spin text-emerald-600" size={40} />
    </div>
  );

  return (
    <div className="space-y-6 relative" dir="rtl">

      {/* Toast */}
      {notification.show && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-10 duration-300 bg-white ${
          notification.type === 'success' ? 'border-emerald-100 text-emerald-700' : 'border-red-100 text-red-700'
        }`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className="text-red-500" />}
          <span className="text-sm font-bold">{notification.message}</span>
          <button onClick={() => setNotification(n => ({ ...n, show: false }))} className="mr-4 text-gray-400"><X size={16} /></button>
        </div>
      )}

      {/* Header & Search */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight uppercase">إدارة المنتجات</h2>
          <p className="text-gray-500 font-bold mt-1">عرض وتعديل مخزون benmousaherbs.tn</p>
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="ابحث باسم المنتج أو القسم..."
              className="w-full pr-12 pl-4 py-3 border border-gray-400 rounded-2xl outline-none font-bold bg-white focus:border-emerald-600 transition-all text-right"
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Link to="/admin/addProduct" className="bg-emerald-600 text-white p-4 rounded-2xl font-black hover:bg-emerald-700 transition-all">
            <Plus size={24} />
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: 'new',      label: 'NEW',         icon: <Sparkles size={12} />, active: 'bg-blue-600 border-blue-600 text-white' },
          { key: 'mostSold', label: 'BEST SELLER', icon: <Flame size={12} />,    active: 'bg-orange-500 border-orange-500 text-white' },
          { key: 'featured', label: 'FEATURED',    icon: <Star size={12} />,     active: 'bg-amber-500 border-amber-500 text-white' },
          { key: 'pack',     label: 'PACK',        icon: <Package size={12} />,  active: 'bg-purple-600 border-purple-600 text-white' },
        ].map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setTypeFilter(t => t === f.key ? 'all' : f.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${
              typeFilter === f.key ? f.active : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'
            }`}
          >
            {f.icon} {f.label}
          </button>
        ))}

        {/* Status pills — independent from type filter */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(s => s === opt.value ? '' : opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${
                statusFilter === opt.value ? `${opt.color} text-white` : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === opt.value ? 'bg-white' : opt.color}`} />
              {opt.label}
            </button>
          ))}
        </div>

        {/* Clear all */}
        {(typeFilter !== 'all' || statusFilter) && (
          <button
            type="button"
            onClick={() => { setTypeFilter('all'); setStatusFilter(''); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-black border border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500 transition-all"
          >
            <X size={11} /> مسح
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-400 rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-right border-collapse min-w-[700px]">
          <thead className="bg-gray-50 border-b border-gray-400">
            <tr>
              <th className="p-5 text-sm font-black text-gray-700">المنتج</th>
              <th className="p-5 text-sm font-black text-gray-700">الأقسام والفروع</th>
              <th className="p-5 text-sm font-black text-gray-700">الحالة</th>
              <th className="p-5 text-sm font-black text-gray-700">الأسعار</th>
              <th className="p-5 text-sm font-black text-gray-700 text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredProducts.map((product, index) => {
              const isLast = filteredProducts.length === index + 1;
              const stockBadge = STOCK_BADGE[product.status] || STOCK_BADGE.available;
              return (
                <tr
                  key={product._id}
                  ref={isLast ? lastProductElementRef : null}
                  className={`hover:bg-gray-50/50 transition-colors ${product.isArchived ? 'bg-gray-50/80' : ''}`}
                >
                  <td className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 border border-gray-400 rounded-2xl overflow-hidden bg-white flex-shrink-0 relative group/img">
                        <img
                          src={product.images?.[0] ? `${backendURL}/${product.images[0]}` : '/placeholder.png'}
                          className={`w-full h-full object-contain p-1 ${product.isArchived ? 'grayscale opacity-50' : ''}`}
                          alt="product"
                        />
                        {product.images?.[0] && (
                          <a
                            href={`${backendURL}/${product.images[0]}`}
                            download
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                            title="تحميل الصورة"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                            </svg>
                          </a>
                        )}
                      </div>
                      <div>
                        <p className={`font-black mb-1 ${product.isArchived ? 'text-gray-400' : 'text-gray-900'}`}>{getLabel(product.name)}</p>
                        <div className="flex items-center gap-1 flex-wrap">
                          {product.isPack && (
                            <span className="text-[8px] font-black bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-md">PACK</span>
                          )}
                          {product.isMostSold && (
                            <span className="text-[8px] font-black bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-md flex items-center gap-0.5"><Flame size={8} /> BEST SELLER</span>
                          )}
                          {product.isNewProduct && (
                            <span className="text-[8px] font-black bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-md flex items-center gap-0.5"><Sparkles size={8} /> NEW</span>
                          )}
                          {product.featured && (
                            <span className="text-[8px] font-black bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-md flex items-center gap-0.5"><Star size={8} /> FEATURED</span>
                          )}
                        </div>
                        {!product.isArchived && (
                          <Link to={`/products/${product.slug}`} target="_blank" className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 hover:underline">
                            معاينة بالموقع <ExternalLink size={10} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(product.category) ? product.category : [product.category]).filter(Boolean).map((cat, idx) => (
                          <span key={idx} className="text-[9px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                            {getLabel(cat?.name)}
                          </span>
                        ))}
                      </div>
                      {product.subCategory?.length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center">
                          <ChevronLeft size={10} className="text-gray-300" />
                          {(Array.isArray(product.subCategory) ? product.subCategory : [product.subCategory]).filter(Boolean).map((sub, idx) => (
                            <span key={idx} className="text-[9px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-md">
                              {getLabel(sub?.name)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col gap-1.5">
                      {product.isArchived ? (
                        <span className="flex items-center gap-1.5 text-[10px] font-black text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full w-fit">
                          <Archive size={12} /> مؤرشف
                        </span>
                      ) : (
                        <span className={`flex items-center gap-1.5 text-[10px] font-black border px-3 py-1 rounded-full w-fit ${stockBadge.cls}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {stockBadge.label}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col gap-1">
                      {product.variants?.slice(0, 2).map((v, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px]">
                          <span className="text-gray-400 font-bold">{v.grammage}:</span>
                          <span className="font-black text-gray-900">{Number(v.price).toFixed(3)} DT</span>
                          {v.isOnSale && v.discountPrice > 0 && (
                            <span className="text-[9px] font-black text-red-500">← {Number(v.discountPrice).toFixed(3)}</span>
                          )}
                        </div>
                      ))}
                      {product.variants?.length > 2 && <span className="text-[9px] text-gray-400">+{product.variants.length - 2} المزيد</span>}
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/admin/edit-product/${product.slug}`}
                        className="p-2.5 border border-gray-400 rounded-xl hover:bg-white text-gray-700 transition-all hover:border-emerald-600"
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleToggleArchive(product._id, product.isArchived)}
                        className={`p-2.5 border border-gray-400 rounded-xl transition-all ${
                          product.isArchived
                            ? 'hover:bg-emerald-50 text-emerald-600 hover:border-emerald-600'
                            : 'hover:bg-amber-50 text-amber-600 hover:border-amber-600'
                        }`}
                        title={product.isArchived ? 'استعادة' : 'أرشفة'}
                      >
                        {product.isArchived ? <RefreshCcw size={18} /> : <Archive size={18} />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {loading && (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-emerald-600" size={32} />
          </div>
        )}

        {filteredProducts.length === 0 && !loading && (
          <div className="p-16 text-center flex flex-col items-center gap-4 text-gray-400">
            <Package size={64} className="opacity-20" />
            <p className="font-black text-xl">لم يتم العثور على أي منتج</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageProducts;
